import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { createStrapiSearchClient } from './strapiSearchClient';
import { getSearchDestination, getSearchTitle, getSearchTypeLabel } from './searchResultUtils';
import './navbarSearch.css';

export default function NavbarSearch({ id, mobile = false, onNavigate }) {
  const { t } = useTranslation();
  const client = useMemo(() => createStrapiSearchClient(), []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [hits, setHits] = useState([]);
  const [status, setStatus] = useState('idle');
  const term = query.trim();
  const visible = open && term.length >= 2;
  const allResults = `/searchpage?q=${encodeURIComponent(term)}`;

  useEffect(() => {
    let current = true;
    setHits([]);
    setActive(-1);
    if (!open || term.length < 2) { setStatus('idle'); return undefined; }
    setStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const { results } = await client.search([{ indexName: 'mof_public_search', params: { query: term, hitsPerPage: 5 } }]);
        if (current) { setHits(results[0].hits); setStatus('ready'); }
      } catch (error) { if (current) setStatus('error'); }
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [client, term, open]);

  const suggestions = hits.map(hit => {
    const title = getSearchTitle(hit);
    return {
      title,
      label: t(getSearchTypeLabel(hit)),
      path: getSearchDestination(hit, allResults),
    };
  });
  const navigate = path => {
    setOpen(false);
    setActive(-1);
    if (/^(https?:\/\/|mailto:)/i.test(path)) window.location.assign(path);
    else onNavigate(path);
  };
  const highlight = title => {
    const index = title.toLocaleLowerCase().indexOf(term.toLocaleLowerCase());
    return index < 0 ? title : <>{title.slice(0, index)}<mark>{title.slice(index, index + term.length)}</mark>{title.slice(index + term.length)}</>;
  };

  return (
    <form className={`portal-autocomplete${mobile ? ' portal-autocomplete--mobile' : ''}`} role="search"
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}
      onSubmit={event => { event.preventDefault(); navigate(visible && active >= 0 && suggestions[active] ? suggestions[active].path : allResults); }}>
      <div className="portal-autocomplete__field">
        <SearchIcon className="portal-autocomplete__search-icon" aria-hidden="true" />
        <input id={id} name="q" role="combobox" aria-label={t('Search the portal')} aria-autocomplete="list"
          aria-expanded={visible} aria-controls={visible ? `${id}-list` : undefined}
          aria-activedescendant={visible && active >= 0 ? `${id}-option-${active}` : undefined}
          placeholder={t('Search the portal')} autoComplete="off" value={query}
          onFocus={() => setOpen(true)} onChange={event => { setQuery(event.target.value); setHits([]); setActive(-1); setStatus('loading'); setOpen(true); }}
          onKeyDown={event => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'Escape') { event.preventDefault(); setOpen(false); setActive(-1); }
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && term.length >= 2) {
              event.preventDefault(); setOpen(true);
              setActive(value => event.key === 'ArrowDown' ? (value + 1) % (suggestions.length + 1) : (value <= 0 ? suggestions.length : value - 1));
            }
          }} />
        {query && <button className="portal-autocomplete__clear" type="button" aria-label={t('Clear search')}
          onClick={() => { setQuery(''); document.getElementById(id)?.focus(); }}><CloseIcon fontSize="small" /></button>}
        <button className="portal-autocomplete__submit" type="submit" aria-label={t('Submit search')}><ArrowForwardIcon fontSize="small" /></button>
      </div>
      {visible && <div className="portal-autocomplete__panel">
        <div className="portal-autocomplete__heading">{t('Suggested results')}</div>
        <div role="status" className="portal-autocomplete__status">
          {status === 'loading' ? t('Searching…') : status === 'error' ? t('Suggestions are temporarily unavailable. Try the full search.') :
            status === 'ready' && !hits.length ? t('No matches yet. Try a different keyword.') : ''}
        </div>
        <ul id={`${id}-list`} role="listbox" aria-label={t('Search suggestions')}>
          {suggestions.map((item, index) => <li key={`${item.path}-${index}`} role="option" id={`${id}-option-${index}`}
            aria-selected={active === index} className="portal-autocomplete__option" onMouseDown={event => event.preventDefault()}
            onMouseEnter={() => setActive(index)} onClick={() => navigate(item.path)}>
            <span className="portal-autocomplete__document"><DescriptionOutlinedIcon fontSize="small" /></span>
            <span className="portal-autocomplete__copy"><span className="portal-autocomplete__title">{highlight(item.title)}</span>
              <span className="portal-autocomplete__category">{item.label}</span></span>
            <ArrowForwardIcon className="portal-autocomplete__arrow" fontSize="small" />
          </li>)}
          <li role="option" id={`${id}-option-${suggestions.length}`} aria-selected={active === suggestions.length}
            className="portal-autocomplete__all" onMouseDown={event => event.preventDefault()}
            onMouseEnter={() => setActive(suggestions.length)} onClick={() => navigate(allResults)}>
            <span>{t('View all results for')} <strong>“{term}”</strong></span><ArrowForwardIcon fontSize="small" />
          </li>
        </ul>
        <div className="portal-autocomplete__hint">{t('↑ ↓ to navigate · Enter to select · Esc to close')}</div>
      </div>}
    </form>
  );
}
